import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { ApiResponse, ApiError } from '@/types/message'

/**
 * Auth0-aware API client that retrieves tokens from a provided getter function
 * This allows it to work with Auth0's token management
 */
class Auth0ApiClient {
  private client: AxiosInstance
  private tokenGetter: (() => Promise<string>) | null = null
  private isRefreshingToken = false
  private failedRequestsQueue: Array<{
    resolve: (token: string) => void
    reject: (error: unknown) => void
  }> = []

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL || '/api') {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  /**
   * Set the token getter function (should be called with getAccessToken from Auth0Context)
   */
  setTokenGetter(getter: () => Promise<string>) {
    this.tokenGetter = getter
  }

  private setupInterceptors() {
    // Request interceptor to add auth token from Auth0
    this.client.interceptors.request.use(
      async (config) => {
        if (this.tokenGetter) {
          try {
            const token = await this.tokenGetter()
            if (token) {
              config.headers.Authorization = `Bearer ${token}`
            }
          } catch (error) {
            console.error('Failed to get access token:', error)
            // Continue with request even if token fetch fails
          }
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        const status = error.response?.status

        // Never retry 429 (rate limit) errors - fail immediately
        if (status === 429) {
          const apiError: ApiError = {
            message: error.response?.data?.message || 'Rate limit exceeded. Please try again later.',
            code: error.response?.data?.code || 'RATE_LIMIT_EXCEEDED',
            details: error.response?.data?.details
          }
          return Promise.reject(apiError)
        }

        // If we get a 401, the token might be expired - Auth0 will handle refresh automatically
        // Only retry once to prevent infinite loops leading to 429 rate limiting
        if (status === 401 && this.tokenGetter && !originalRequest._retry) {
          // If token refresh is already in progress, queue this request
          if (this.isRefreshingToken) {
            return new Promise((resolve, reject) => {
              this.failedRequestsQueue.push({
                resolve: (token: string) => {
                  originalRequest.headers.Authorization = `Bearer ${token}`
                  resolve(this.client.request(originalRequest))
                },
                reject: (err: unknown) => {
                  reject(err)
                }
              })
            })
          }

          originalRequest._retry = true
          this.isRefreshingToken = true

          try {
            // Try to get a fresh token (Auth0 SDK will refresh if needed)
            const token = await this.tokenGetter()
            if (token) {
              // Process all queued requests with the new token
              this.failedRequestsQueue.forEach(({ resolve }) => resolve(token))
              this.failedRequestsQueue = []

              // Retry the original request with the fresh token
              originalRequest.headers.Authorization = `Bearer ${token}`
              return this.client.request(originalRequest)
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)

            // Reject all queued requests
            this.failedRequestsQueue.forEach(({ reject }) => reject(refreshError))
            this.failedRequestsQueue = []

            // Let the error fall through
          } finally {
            this.isRefreshingToken = false
          }
        }

        const apiError: ApiError = {
          message: error.response?.data?.message || error.message || 'An error occurred',
          code: error.response?.data?.code,
          details: error.response?.data?.details
        }

        return Promise.reject(apiError)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<ApiResponse<T>>(url, config)
    return response.data.data
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config)
    return response.data.data
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config)
    return response.data.data
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config)
    return response.data.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<ApiResponse<T>>(url, config)
    return response.data.data
  }

  /**
   * GET that returns the full response envelope (including pagination metadata)
   * rather than unwrapping to `data`. Use for paginated endpoints whose
   * envelope contains both `data` and `pagination`.
   */
  async getEnvelope<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config)
    return response.data
  }

  async upload<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data
  }
}

export const auth0ApiClient = new Auth0ApiClient()
